/**
 * Wheel of Fortune Game
 * Beautiful Pixi.js implementation with GSAP animations
 * Hybrid approach: PNG overlays + procedural segments
 */

import { Container } from "pixi.js";
import * as PIXI from "pixi.js";
import gsap from "gsap";
import { WheelRendererHybrid, type WheelTextures } from "./WheelRendererHybrid";
import type { GameOutcome, GameState, DebugOptions, WheelConfig } from "../runtime/types";

export interface WheelGameOptions {
  config: WheelConfig;
  textures: WheelTextures;  // NEW: Preloaded textures
  width: number;
  height: number;
  debug?: DebugOptions;
  onStateChange?: (state: GameState) => void;
  onOutcome?: (outcome: GameOutcome) => void;
}

/**
 * Wheel Game
 * Coordinates wheel rendering and spin animation
 */
export class WheelGame extends Container {
  private config: WheelConfig;
  private wheelRenderer: WheelRendererHybrid;
  private onStateChange?: (state: GameState) => void;
  private onOutcome?: (outcome: GameOutcome) => void;

  private currentState: GameState = "idle";
  private isSpinning: boolean = false;
  private currentRotation: number = 0;
  private spinTween: gsap.core.Tween | null = null;
  private bulbAnimationTicker: (() => void) | null = null;

  constructor(options: WheelGameOptions) {
    super();

    this.config = options.config;
    this.onStateChange = options.onStateChange;
    this.onOutcome = options.onOutcome;

    const centerX = options.width / 2;
    const centerY = options.height / 2;
    // Balanced radius that fits well with rim and lights
    const radius = Math.min(options.width, options.height) * 0.36;

    // Create hybrid wheel renderer (PNG overlays + procedural segments)
    this.wheelRenderer = new WheelRendererHybrid(this.config, options.textures, radius);
    this.wheelRenderer.x = centerX;
    this.wheelRenderer.y = centerY;
    this.addChild(this.wheelRenderer);

    // Make wheel interactive
    this.wheelRenderer.eventMode = 'static';
    this.wheelRenderer.cursor = 'pointer';
    this.wheelRenderer.on('pointerdown', () => {
      if (!this.isSpinning) {
        this.simulateSpin();
      }
    });

    // Start bulb animation ticker
    this.startBulbAnimation();
  }

  /**
   * Start continuous bulb light animation
   */
  private startBulbAnimation(): void {
    this.bulbAnimationTicker = () => {
      this.wheelRenderer.animateBulbs();
    };
  }

  /**
   * Get the ticker function for external ticker registration
   * (GamePlaygroundPage will add this to PIXI.Application ticker)
   */
  public getBulbAnimationTicker(): (() => void) | null {
    return this.bulbAnimationTicker;
  }

  /**
   * Simulate a spin (for testing)
   */
  private simulateSpin(): void {
    const segments = this.config.segments;
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    
    // Pick random segment
    let random = Math.random() * totalWeight;
    let selectedSegment = segments[0];
    
    for (const segment of segments) {
      random -= segment.weight;
      if (random <= 0) {
        selectedSegment = segment;
        break;
      }
    }

    // Calculate target angle
    let cumulativeAngle = 0;
    for (const segment of segments) {
      const segmentAngle = (segment.weight / totalWeight) * 360;
      
      if (segment.outcomeKey === selectedSegment.outcomeKey) {
        const targetAngle = cumulativeAngle + segmentAngle / 2;
        
        this.spin({
          outcomeKey: selectedSegment.outcomeKey,
          outcomeValue: { label: selectedSegment.label },
          targetAngle,
          totalRotation: 5 * 360 + targetAngle
        });
        break;
      }
      
      cumulativeAngle += segmentAngle;
    }
  }

  /**
   * Spin the wheel to a predetermined outcome
   * STABLE: Always clockwise, consistent duration, normalized rotation
   */
  public spin(outcome: GameOutcome): void {
    if (this.isSpinning) {
      console.warn('[WheelGame] Spin already in progress');
      return;
    }

    // Kill any previous tween and stop all wheel animations
    if (this.spinTween) {
      this.spinTween.kill();
      this.spinTween = null;
    }
    
    // Rotate only the spinning container (segments), not the entire wheel
    const wheel = this.wheelRenderer.getSpinContainer();
    gsap.killTweensOf(wheel); // Ensure no stray tweens

    this.isSpinning = true;
    this.setState("playing");

    const duration = (this.config.spinDuration || 4000) / 1000;
    
    // ALWAYS CLOCKWISE: fixed number of full spins + target angle
    const baseSpins = 6; // Fixed 6 full rotations
    const targetAngleDeg = outcome.targetAngle || 0;
    const targetAngleRad = (targetAngleDeg * Math.PI) / 180;
    
    // Normalize current rotation to [0, 2π) to prevent float accumulation
    const TAU = Math.PI * 2;
    const normalizedCurrent = ((this.currentRotation % TAU) + TAU) % TAU;
    
    // Calculate target rotation: always add positive offset
    const targetRotation = normalizedCurrent + (baseSpins * TAU) + targetAngleRad;

    // Find winning segment index
    const winningSegmentIndex = this.findSegmentIndex(outcome.outcomeKey);

    // Perform spin animation with GSAP (single tween, deterministic)
    this.spinTween = gsap.to(wheel, {
      rotation: targetRotation,
      duration,
      ease: 'power4.out', // Consistent easing
      onComplete: () => {
        // Normalize final rotation to [0, 2π)
        const finalNormalized = ((wheel.rotation % TAU) + TAU) % TAU;
        wheel.rotation = finalNormalized;
        this.currentRotation = finalNormalized;
        
        this.isSpinning = false;
        this.spinTween = null;
        this.setState("resolved");
        
        // Win moment celebration
        this.celebrateWin(winningSegmentIndex);
        
        if (this.onOutcome) {
          this.onOutcome(outcome);
        }
      }
    });
  }

  /**
   * Find segment index by outcome key
   */
  private findSegmentIndex(outcomeKey: string): number {
    return this.config.segments.findIndex(seg => seg.outcomeKey === outcomeKey);
  }

  /**
   * Win moment celebration effects
   */
  private celebrateWin(segmentIndex: number): void {
    if (segmentIndex < 0) return;

    // Highlight winning segment
    this.wheelRenderer.highlightWinningSegment(segmentIndex);

    // Light confetti burst
    this.createConfetti();
  }

  /**
   * Create simple confetti particles (Pixi v8)
   */
  private createConfetti(): void {
    const confettiContainer = new Container();
    confettiContainer.x = this.wheelRenderer.x;
    confettiContainer.y = this.wheelRenderer.y;
    this.addChild(confettiContainer);

    const colors = [0xFFD700, 0xFF6B9D, 0x4ECDC4, 0xFFE66D, 0x95E1D3];
    const particleCount = 30;

    for (let i = 0; i < particleCount; i++) {
      const particle = new PIXI.Graphics();
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      // Pixi v8 API
      particle.circle(0, 0, Math.random() * 4 + 2);
      particle.fill({ color, alpha: 1 });

      particle.x = 0;
      particle.y = 0;

      confettiContainer.addChild(particle);

      // Random trajectory
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 150 + 50;
      const targetX = Math.cos(angle) * distance;
      const targetY = Math.sin(angle) * distance - 50; // Bias upward

      gsap.to(particle, {
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 1 + Math.random() * 0.5,
        ease: 'power2.out'
      });
    }

    // Remove confetti container after animation
    setTimeout(() => {
      this.removeChild(confettiContainer);
    }, 1500);
  }

  private setState(state: GameState): void {
    this.currentState = state;
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  /**
   * Update method called every frame (required by playground)
   */
  public update(_delta: number): void {
    // Animation is handled by GSAP, no frame-by-frame updates needed
  }

  /**
   * Set icon textures for segment icons
   */
  public setIconTextures(icons: import('./iconLoader').IconTextures): void {
    this.wheelRenderer.setIconTextures(icons);
  }

  /**
   * Update configuration
   */
  public updateConfig(config: WheelConfig): void {
    this.config = config;
    
    // Kill previous tween
    if (this.spinTween) {
      this.spinTween.kill();
      this.spinTween = null;
    }
    
    // Stop any ongoing animations
    const wheel = this.wheelRenderer.getSpinContainer();
    gsap.killTweensOf(wheel);
    
    // Reset rotation
    wheel.rotation = 0;
    this.currentRotation = 0;
    
    // Update renderer with new config
    this.wheelRenderer.updateConfig(config);
    
    // Reset state
    this.isSpinning = false;
    this.setState("idle");
  }

  /**
   * Get current state
   */
  public getState(): GameState {
    return this.currentState;
  }

  /**
   * Reset the wheel to initial state
   */
  public reset(): void {
    // Kill previous tween
    if (this.spinTween) {
      this.spinTween.kill();
      this.spinTween = null;
    }
    
    const wheel = this.wheelRenderer.getSpinContainer();
    gsap.killTweensOf(wheel);
    wheel.rotation = 0;
    this.currentRotation = 0;
    this.isSpinning = false;
    this.setState("idle");
  }

  /**
   * Update debug options (for playground compatibility)
   */
  public updateDebug(_debug: DebugOptions): void {
    // Debug rendering can be added here if needed
  }
}
